import {
  Button,
  Select,
  Text,
  Notification,
  Input,
  Radio,
  Checkbox,
} from '@perch-labs/shrike';
import { copyFor } from 'config';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Grid, LeftColumnGridCell, RightColumnGridCell, Row } from 'styles';
import { FileRejection } from 'react-dropzone';
import { trackEvent } from 'domain/tracking';
import { FetchResult, useMutation, useQuery } from '@apollo/client';
import { snakeCase } from 'lodash';
import { NOTIFICATION_DISPLAY_TIME } from 'domain/ux';
import { LoadingModule } from 'components/loading';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import {
  AddProofDocRowChild,
  StyledCrate,
  StyledLegend,
} from './add-proof-doc-upload.style';
import { FETCH_LMI_PROOF_DOC_LABEL_OPTIONS } from '../../services/fetch-lmi-proof-options';
import {
  DocumentUploader,
  UploadedDocument,
  addFileProps,
} from '../document-uploader/document-uploader.component';
import { UPLOAD_LMI_PROOF_DOC } from '../../lmi-proof-doc-upload.api';
import { findUtilityById } from '../../check-lmi-eligibility.utils';

const getCopy = copyFor('settings.checkLmiEligibility.addDocument');

export interface AddProofDocUploadProps {
  toggleAddDocument: () => void;
  utilityDropDown: {
    text: string;
    value: number;
    utilityId: number;
    state: string;
    stateId: number;
    zipCode: string;
    medicaidCardRequiresThirdPartyVerification: boolean;
  }[];
  user?: {
    id: number;
    originationSource: {
      funnel: string | null;
    } | null;
    lastName: string | null;
  };
  refetchLmiDocs: () => void;
  setIsTableDataLoading: (value: boolean) => void;
  data:
    | ({
        proofDocUrls: (string | null)[];
        proofDocName: string | null;
        proofDocTypes: (string | null)[] | null;
        proofDocStatus: string | null;
        proofDocUploadedAt: string | null;
        uaLmiSourceTypeId: number | null;
        utilityAccountId: number | null;
        utilityName: string | null;
        utilityAccountNumber: string | null;
        utilityServiceAddress: string | null;
      } | null)[]
    | undefined;
  selectedUtilityAccountId?: number | null;
  selectedUaLMISourceType?: string | null;
}

const RelationshipSelector = ({ relationship, setRelationship }) => {
  return (
    <>
      <StyledLegend>
        <Text textStyle="heading500">
          Relationship<span style={{ color: '#AD0B1A' }}>*</span>
        </Text>
      </StyledLegend>
      <Radio
        color="primary"
        error=""
        margin={{ bottom: '8px', top: '8px' }}
        name="relationshipGroup"
        onChange={e => setRelationship(e)}
        value="self"
        checked={relationship === 'self'}
      >
        <Text>Myself</Text>
      </Radio>
      <Radio
        color="primary"
        error=""
        margin={{ bottom: '8px' }}
        name="relationshipGroup"
        onChange={e => setRelationship(e)}
        value="other"
        checked={relationship === 'other'}
      >
        <Text>Another member of my household</Text>
      </Radio>
    </>
  );
};

export const AddProofDocUpload: React.FC<AddProofDocUploadProps> = ({
  toggleAddDocument,
  utilityDropDown,
  refetchLmiDocs,
  setIsTableDataLoading,
  data,
  user,
  selectedUtilityAccountId,
  selectedUaLMISourceType,
}) => {
  const [failure, setFailure] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const { addNotification } = useContext(Notification.Context);
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedDocument[]>([]);

  const [utilitySelection, setUtilitySelection] = useState({
    account: utilityDropDown[0]?.value,
    utility: utilityDropDown[0]?.utilityId,
    state: utilityDropDown[0]?.state,
  });

  const handleUtilityChange = value => {
    const utility = findUtilityById(utilityDropDown, Number(value));
    setUtilitySelection({
      account: Number(value),
      utility: utility?.utilityId ?? 0,
      state: utility?.state || '',
    });
  };

  const [disableRequest, setDisableRequest] = useState(false);
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [enableManualCaptureOfProofs, setEnableManualCaptureOfProofs] =
    useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (
      selectedUtilityAccountId &&
      utilitySelection.account !== Number(selectedUtilityAccountId)
    ) {
      const utility = findUtilityById(
        utilityDropDown,
        Number(selectedUtilityAccountId)
      );
      setUtilitySelection({
        account: Number(selectedUtilityAccountId),
        utility: utility?.utilityId ?? 0,
        state: utility?.state || '',
      });
    }
  }, [selectedUtilityAccountId, utilityDropDown]);

  const ldClient = useLDClient();
  const custom = useMemo(
    () => ({
      funnel: user?.originationSource?.funnel ?? 'communitySolar',
      utilityId: utilitySelection.utility!,
      state: utilitySelection.state,
    }),
    [
      user?.originationSource?.funnel,
      utilitySelection.utility,
      utilitySelection.state,
    ]
  );

  useEffect(() => {
    if (ldClient && custom.utilityId && custom.state && user?.id) {
      ldClient.identify({
        key: `user_${user.id}_utility_${custom.utilityId}`,
        custom,
      });
    }
  }, [ldClient, custom, user?.id]);

  const flag = useFlags();

  useEffect(() => {
    setDisableRequest(
      !flag.csLmiProofDocsState?.includes(utilitySelection.state)
    );
    setEnableManualCaptureOfProofs(flag.enableManualCaptureOfProofs);
  }, [utilitySelection.state, flag]);

  const { data: proofDocLabelData } = useQuery(
    FETCH_LMI_PROOF_DOC_LABEL_OPTIONS,
    {
      variables: {
        utilityId: utilitySelection.utility,
        category: 'proof_docs',
      },
      skip: !utilitySelection.utility,
    }
  );

  const programOptions = useMemo(() => {
    const raw: Record<string, string | string[]> | null =
      proofDocLabelData?.lmiProofDocLabelOptions?.data ?? null;
    if (!raw) return undefined;
    return Object.entries(raw).map(([key, label]) => ({
      value: key,
      text: Array.isArray(label) ? label[0] : label,
    }));
  }, [proofDocLabelData]);

  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedProofDocInReview, setSelectedProofDocInReview] =
    useState(false);

  useEffect(() => {
    if (selectedUaLMISourceType && programOptions) {
      const matchingOption = programOptions.find(
        option => snakeCase(option.value) === selectedUaLMISourceType
      );
      if (matchingOption) {
        setSelectedProgram(matchingOption.value);
      }
    }
  }, [selectedUaLMISourceType, programOptions]);

  const [mutate] = useMutation(UPLOAD_LMI_PROOF_DOC);

  const isProofItemInPendingReview = (dataItem: any, proofDocName: string) => {
    return (
      dataItem &&
      dataItem.proofDocName === proofDocName &&
      dataItem.proofDocStatus === 'pending_review' &&
      dataItem.utilityAccountId === utilitySelection.account
    );
  };

  // Check if there's a medicaid card in pending review that allows verification document upload
  // This is only allowed for utilities that require third party verification (all except Minnesota)
  const canUploadMedicaidCardVerificationDocument = () => {
    return (
      data?.some((dataItem: any) => {
        return (
          isProofItemInPendingReview(dataItem, 'medicaid') &&
          dataItem.proofDocTypes?.includes('card') &&
          utilityDropDown.find(u => u.value === dataItem.utilityAccountId)
            ?.medicaidCardRequiresThirdPartyVerification
        );
      }) ?? false
    );
  };

  useEffect(() => {
    let disableSubmit = false;
    let selectedProofDocInReview = false;

    // Check if there's a proof of the selected program in pending review
    const hasSelectedProgramInPendingReview =
      data?.some((dataItem: any) => {
        return isProofItemInPendingReview(dataItem, snakeCase(selectedProgram));
      }) ?? false;

    if (hasSelectedProgramInPendingReview) {
      // Special case for medicaid: If medicaid is in pending review, allow upload of verification document
      if (
        flag.csMedicaidCardVerificationEnhancement &&
        selectedProgram === 'medicaid' &&
        canUploadMedicaidCardVerificationDocument()
      ) {
        disableSubmit = false;
        selectedProofDocInReview = false;
      } else {
        // For all other cases, disable submit if proof is in pending review
        disableSubmit = true;
        selectedProofDocInReview = true;
      }
    }

    if (!selectedProgram) {
      disableSubmit = true;
    }
    if (
      enableManualCaptureOfProofs &&
      (name.trim() === '' || relationship === '' || checked === false)
    ) {
      disableSubmit = true;
    }
    setDisableSubmit(disableSubmit);
    setSelectedProofDocInReview(selectedProofDocInReview);
  }, [
    selectedProgram,
    utilitySelection.account,
    name,
    relationship,
    checked,
    data,
    utilityDropDown,
    enableManualCaptureOfProofs,
  ]);

  const uploadFilesSequentially = async () => {
    let results: FetchResult[] = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const file of uploadedFiles) {
      try {
        const input: Record<string, any> = {
          utilityAccountId: utilitySelection.account,
          documentType: snakeCase(selectedProgram),
          image: file.base64Image,
          source: 'customer_dashboard',
          proofType: file.proofType,
        };
        if (name.trim() !== '') {
          input.name = name.trim();
        }
        if (relationship.trim() !== '') {
          input.relationship = relationship;
        }
        const result = await mutate({
          variables: {
            input,
          },
        });
        results.push(result);
      } catch (error) {
        results = [];
      }
    }
    return results;
  };

  const handleSaveButtonClick = useCallback(async () => {
    setLoading(true);
    setIsTableDataLoading(true);
    const results = await uploadFilesSequentially();
    const successfulUploads = results.filter(
      result => result && result.data?.uploadLmiProofDoc !== null
    );
    if (successfulUploads.length === uploadedFiles.length) {
      addNotification({
        displayTime: NOTIFICATION_DISPLAY_TIME,
        message: getCopy('successToast'),
        variant: 'success',
      });
      trackEvent('track_user_uploaded_lmi_doc', {
        metadata: { selectedProgram, account: utilitySelection.account },
      });
    } else {
      addNotification({
        displayTime: NOTIFICATION_DISPLAY_TIME,
        message: getCopy('failureToast'),
        variant: 'error',
      });
    }
    setIsTableDataLoading(false);
    setLoading(false);
    refetchLmiDocs();
    toggleAddDocument();
  }, [
    toggleAddDocument,
    uploadedFiles,
    utilitySelection.account,
    selectedProgram,
    mutate,
    addNotification,
  ]);

  const content = (
    <Row>
      <AddProofDocRowChild>
        <Text textStyle="heading600" tag="h2" margin={{ bottom: '16px' }}>
          {getCopy('title')}
        </Text>
        <Grid width="100%">
          <LeftColumnGridCell>
            <Select
              label={getCopy('utilityDropdown')}
              name="utilityDropdown"
              options={utilityDropDown}
              onChange={handleUtilityChange}
              minHeight="0px"
              margin={{ top: '8px' }}
              disabled={uploadedFiles.length > 0}
              value={utilitySelection.account}
            />
            {!disableRequest && (
              <Text textStyle="paragraph400" margin={{ top: '8px' }}>
                {getCopy('utilityInfo')}
              </Text>
            )}
            {enableManualCaptureOfProofs && (
              <>
                <Input
                  errorText={
                    !name.trim() ? 'Please enter your name on document' : ''
                  }
                  id="name"
                  required
                  label="Name on document(s)"
                  name="name"
                  onChange={e => setName(e)}
                  margin={{ top: '8px' }}
                  value={name}
                />
                <RelationshipSelector
                  relationship={relationship}
                  setRelationship={setRelationship}
                />
              </>
            )}
          </LeftColumnGridCell>
          <RightColumnGridCell>
            <Select
              label={getCopy('programDropdown')}
              name="programDropdown"
              options={programOptions}
              onChange={value => {
                setSelectedProgram(value);
              }}
              minHeight="0px"
              margin={{ top: '8px' }}
              withEmptyPlaceholder
              disabled={uploadedFiles.length > 0 || disableRequest}
              required
              value={selectedProgram}
            />
          </RightColumnGridCell>
        </Grid>
        {disableRequest && (
          <Text color="error" margin={{ top: '8px' }}>
            {getCopy('notRequiredForUtility')}
          </Text>
        )}
        {loading ? (
          <LoadingModule variant="generic" margin={{ bottom: '24px' }} />
        ) : (
          <DocumentUploader
            funnel={user?.originationSource?.funnel || 'communitySolar'}
            utilityId={utilitySelection.utility!}
            acceptFileTypes={{
              'image/jpg': ['.jpg', '.jpeg', '.png', '.heic'],
              'application/pdf': ['.pdf'],
            }}
            copy={{ documentType: 'document' }}
            uploadedDocuments={uploadedFiles}
            maxDocuments={enableManualCaptureOfProofs ? 0 : 2}
            pageType={enableManualCaptureOfProofs ? 'multiple' : 'front-back'}
            onDelete={id => {
              setFailure(false);
              setUploadedFiles(uploadedFiles.filter(f => f.id !== id));
            }}
            onUploadFailure={fileRejection => {
              setFailure(true);
              setUploadedFiles([...uploadedFiles, addFileProps(fileRejection)]);
            }}
            onUploadSuccess={(
              file,
              base64Image,
              pageType,
              proofType = 'unknown'
            ) => {
              setFailure(false);
              setUploadedFiles([
                ...uploadedFiles,
                {
                  ...addFileProps(file, base64Image),
                  documentType: selectedProgram,
                  uploadId: `${selectedProgram}-${pageType}`,
                  proofType,
                },
              ]);
            }}
            pending={false}
            hide={
              disableRequest ||
              (enableManualCaptureOfProofs && !selectedProgram)
            }
            showOnlyOthersOption={
              // Show only "Others" option when medicaid special case is met
              // This allows ops to upload verification documents while medicaid card is pending review
              flag.csMedicaidCardVerificationEnhancement &&
              selectedProgram === 'medicaid' &&
              canUploadMedicaidCardVerificationDocument()
            }
          />
        )}
        {selectedProofDocInReview && (
          <Text color="error" margin={{ top: '8px' }}>
            {getCopy('disableSubmit')}
          </Text>
        )}
        {enableManualCaptureOfProofs && (
          <Checkbox
            checked={checked}
            onChange={() => setChecked(!checked)}
            margin="16px 0 16px 0"
          >
            <Text textStyle="paragraph400" color="secondary">
              {getCopy('checkBox')}
            </Text>
          </Checkbox>
        )}
        <Grid width="100%">
          <LeftColumnGridCell>
            <Button
              disabled={failure || uploadedFiles.length === 0 || disableSubmit}
              margin={{ top: '24px' }}
              onClick={handleSaveButtonClick}
              loading={loading}
            >
              {getCopy('saveDocumentButton')}
            </Button>
            <Button
              backgroundless
              margin={{ top: '24px', left: '8px' }}
              onClick={toggleAddDocument}
            >
              {getCopy('cancelDocumentButton')}
            </Button>
          </LeftColumnGridCell>
        </Grid>
      </AddProofDocRowChild>
    </Row>
  );

  return <StyledCrate>{content}</StyledCrate>;
};

export interface LMIProofDocsUploadProps {
  context: LMIProofDocsUploadContext;
  onDelete: (id: string) => void;
  onUploadFailure: (documentType: string, fileRejection: FileRejection) => void;
  onUploadSuccess: (
    documentType: string,
    file: File,
    base64Image: string
  ) => void;
  pending: boolean;
}

export interface LMIProofDoc {
  documentType: string;
  document: UploadedDocument;
}

export interface LMIProofDocsUploadContext {
  docs: LMIProofDoc[];
  documentTypes: string[];
}
